<template>
    <div :class="cardClass">
        <common-context-menu
            v-if="showContextMenu"
            class="card__context-menu"
            :items="contextMenuItems"
            @modify-privileges="modifyPrivileges"
            @remove-member="openRemovePracticeMemberModal"
        ></common-context-menu>

        <template v-if="isStandalone">
            <div class="card__connections-count">
                <span v-if="showPending || physician.isInvitee" class="card__connection-pending">
                    {{ $t('elements.referrals.card.pending') }}
                </span>
                <p v-else-if="physician.connectionCount">
                    <b>{{ physician.connectionCount }}</b>
                    <span>{{ $t('elements.referrals.card.connections') }}</span>
                </p>
            </div>

            <div class="card__network-status">
                <network-inlusion-status
                    :targetId="physician.id"
                    :targetType="targetType"
                    :isInMyNetwork="isInMyNetwork"
                    :provider="physician"
                    @emitEvent="emitEvent($event)"
                ></network-inlusion-status>
            </div>
        </template>
        <div v-if="isSelectionAllowed" class="card__checkbox-input" :class="{ 'card__checkbox-input--checked': checked }">
            <common-button
                v-if="physician?.practice?.subscription !== PracticePlanEnum.NETWORK"
                :label="checked ? $t('common.microcopy.selected') : $t('common.microcopy.to_select_click')"
                :isSubmittable="false"
                :type="checked ? 'primary' : 'secondary'"
                @click="selectionChanged(physician.id)"
            ></common-button>
        </div>
        <div v-if="isPracticeNetwork" class="card__checkbox-input">
            <p class="direct-referral__invite">
                {{ $t('modals.make_direct_referal_modal.inivte_billing.default_plan') }}
            </p>
            <p class="direct-referral__invite">
                {{ $t('modals.make_direct_referal_modal.inivte_billing.click') }}
                <span @click="inviteUserToSubscribe(physician.id)">
                    {{ $t('modals.make_direct_referal_modal.inivte_billing.here') }}
                </span>
                {{ $t('modals.make_direct_referal_modal.inivte_billing.premium') }}
            </p>
        </div>
        <div v-if="showCardFavorite" class="card__favorite">
            <card-favorite :user="physician"></card-favorite>
        </div>
        <user-card
            :physician="physician"
            :showMasterUserAuthorities="showMasterUserAuthorities"
            :showInvitedDate="showInvitedDate"
            :showNewUserWarning="showNewUserWarning"
            :showReferralsCount="showReferralsCount"
            :isUserProfileLinkEnabled="isUserProfileLinkEnabled"
        ></user-card>

        <div v-if="physician.physicianGroup" class="card__physician-group">
            <p>{{ $t('elements.referrals.card.physician_group') }}</p>
            <p>{{ physician.physicianGroup.name }}</p>
        </div>

        <template v-if="isStandalone">
            <div class="card__cta">
                <div v-if="isPendingApproval" class="card__membership">
                    <p>{{ $t('elements.referrals.card.allow_membership') }}</p>
                    <common-buttons>
                        <common-button
                            :label="$t('elements.referrals.card.buttons.deny')"
                            :isProcessing="isProcessingApproval"
                            type="secondary"
                            size="medium-small"
                            @click="updatePracticeMembershipStatus('deny', {})"
                        ></common-button>
                        <common-button
                            :label="$t('elements.referrals.card.buttons.allow')"
                            :isProcessing="isProcessingApproval"
                            type="primary"
                            size="medium-small"
                            @click="openPracticePrivilegesModal"
                        ></common-button>
                    </common-buttons>
                </div>

                <div v-if="isEligibleForResendingInvitation" class="card__membership">
                    <p>{{ $t('elements.referrals.card.resend_invitation') }}</p>
                    <common-buttons>
                        <common-button
                            :label="$t('elements.referrals.card.buttons.resend_invitation')"
                            :isProcessing="isProcessingApproval"
                            type="primary"
                            size="medium-small"
                            @click="toggleResendInvitationModal()"
                        ></common-button>
                    </common-buttons>
                </div>
                <div v-if="!isPracticeNetwork">
                    <div
                        v-if="!isPendingApproval && showCardAvailability && !checkedValues.length"
                        v-bind="availabilityProps"
                        @emitEvent="emitEvent($event)"
                    ></div>
                </div>
            </div>
        </template>

        <practice-privileges-modal
            v-if="showMasterUserAuthorities"
            :isOpen="isPracticePrivilegesModalOpen"
            :user="physician"
            :isModifyingPrivileges="isModifyingPrivileges"
            @close="closePracticePrivilegesModal"
            @membership-approved="approvePracticeMembership"
            @modify-privileges="modifyUserPrivileges"
        ></practice-privileges-modal>
        <remove-practice-member-modal
            v-if="showMasterUserAuthorities"
            :isOpen="isRemovePracticeMemberModalOpen"
            :user="physician"
            @close="closeRemovePracticeMemberModal"
            @member-removed="practiceMemberRemoved"
        ></remove-practice-member-modal>
        <resend-invitation-modal
            v-if="showMasterUserAuthorities"
            :isOpen="isResendInvitationModalOpen"
            :user="physician"
            @close="toggleResendInvitationModal()"
            @invitation-resent="invitationResent"
        ></resend-invitation-modal>
        <common-notification ref="commonSuccessNotification" :content="notificationMessage"></common-notification>
    </div>
</template>

<script>
    import { importComponents } from '~/helpers'
    import NetworkInlusionStatus from 'components/page-elements/network/NetworkInlusionStatus'
    import UserCard from 'components/page-elements/referrals/UserCard'
    import PracticePrivilegesModal from 'components/page-elements/practice/PracticePrivilegesModal'
    import RemovePracticeMemberModal from 'components/page-elements/practice/RemovePracticeMemberModal'
    import ResendInvitationModal from 'components/page-elements/practice/ResendInvitationModal'
    import PhysicianMixin from 'mixins/PhysicianMixin'
    import UserMembershipStatusEnum from '../../../enums/UserMembershipStatusEnum'
    import UsersAPI from 'api/UsersAPI'
    import UserTypeEnum from '@/enums/UserTypeEnum'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'

    export default {
        mixins: [PhysicianMixin],

        components: {
            ...importComponents(require.context('components/page-elements/referrals-new-card')),
            NetworkInlusionStatus,
            UserCard,
            PracticePrivilegesModal,
            RemovePracticeMemberModal,
            ResendInvitationModal,
        },

        props: {
            isStandalone: { type: Boolean, default: true },
            showNetworkInclusionStatus: { type: Boolean, default: true },
            showMasterUserAuthorities: { type: Boolean, default: false },
            showInvitedDate: { type: Boolean, default: false },
            showNewUserWarning: { type: Boolean, default: false },
            showReferralsCount: { type: Boolean, default: true },
            showFavorite: { type: Boolean, default: true },
            isFixedHeight: { type: Boolean, default: true },
            showDirectReferralAsInnerModal: { type: Boolean, default: false },
            showCardAvailability: { type: Boolean, default: true },
            isUserProfileLinkEnabled: { type: Boolean, default: true },
            isSelectionAllowed: { type: Boolean, default: false },
            checkedValues: { type: Array, default: () => [] },
        },

        data() {
            return {
                isProcessingApproval: false,
                isPracticePrivilegesModalOpen: false,
                isRemovePracticeMemberModalOpen: false,
                isResendInvitationModalOpen: false,
                isModifyingPrivileges: false,
                PracticePlanEnum: PracticePlanEnum,
                notificationMessage: '',
                contextMenuItems: [
                    {
                        label: this.$t('elements.referrals.card.context_menu.modify_privileges'),
                        name: 'modify-privileges',
                    },
                    {
                        label: this.$t('elements.referrals.card.context_menu.remove_member'),
                        name: 'remove-member',
                    },
                ],
            }
        },

        computed: {
            showNetworkStatus() {
                return this.isActiveUser && this.showNetworkInclusionStatus
            },
            showCardFavorite() {
                return this.isActiveUser && !this.isStandalone && this.showFavorite
            },

            isActiveUser() {
                return this.$auth.getActiveMember()
            },
            cardClass() {
                return {
                    card: true,
                    'card__fixed-hight': this.isFixedHeight,
                    'card--is-standalone': this.isStandalone,
                    'card--is-selected': this.checked,
                }
            },

            targetType() {
                return this.isPhysician ? UserTypeEnum.USER : UserTypeEnum.PRACTICE
            },

            targetId() {
                return this.physician?.id
            },

            isPendingApproval() {
                return (
                    this.showMasterUserAuthorities &&
                    this.$auth.getUser().isMasterUser &&
                    this.physician.practiceMembershipStatus === UserMembershipStatusEnum.PENDING
                )
            },
            isPracticeNetwork() {
                return this.physician?.practice?.subscription === PracticePlanEnum.NETWORK
            },
            isEligibleForResendingInvitation() {
                return this.showMasterUserAuthorities && this.$auth.getUser().isMasterUser && this.physician.isInvitee
            },

            isMembershipApproved() {
                return this.physician.practiceMembershipStatus === UserMembershipStatusEnum.APPROVED
            },

            showContextMenu() {
                return (
                    this.showMasterUserAuthorities &&
                    this.isMembershipApproved &&
                    this.$auth.getUser().id !== this.physician?.id &&
                    this.$auth.getActiveMember().id !== this.physician?.id
                )
            },

            userHasPendingInvitation() {
                return this.physician.target && !this.physician.target.dateJoined
            },

            showPending() {
                return this.isPendingApproval || this.userHasPendingInvitation
            },

            isInMyNetwork() {
                return this.physician.isInMyNetwork !== undefined ? this.physician.isInMyNetwork : true
            },

            availabilityProps() {
                if (this.showMasterUserAuthorities) {
                    return this.ctaMasterUserProps
                }
                return this.showDirectReferralAsInnerModal ? this.ctaInnerModalProps : this.ctaProps
            },

            checked() {
                return this.checkedValues.includes(this.physician.id)
            },
        },

        methods: {
            updatePracticeMembershipStatus(action, data) {
                this.isProcessingApproval = true
                UsersAPI.updatePracticeMembershipStatus({ userId: this.physician?.id, action, data }).then(() => {
                    this.isProcessingApproval = false
                    this.emitEvent({ name: 'membership-status-change', payload: action })
                })
            },

            closePracticePrivilegesModal() {
                this.isModifyingPrivileges = false
                this.isPracticePrivilegesModalOpen = false
            },

            openPracticePrivilegesModal() {
                this.isPracticePrivilegesModalOpen = true
            },

            closeRemovePracticeMemberModal() {
                this.isRemovePracticeMemberModalOpen = false
            },

            toggleResendInvitationModal() {
                this.isResendInvitationModalOpen = !this.isResendInvitationModalOpen
            },

            openRemovePracticeMemberModal() {
                this.isRemovePracticeMemberModalOpen = true
            },

            approvePracticeMembership(data) {
                this.updatePracticeMembershipStatus('approve', data)
                this.closePracticePrivilegesModal()
            },

            modifyUserPrivileges(data) {
                UsersAPI.modifyUserPrivileges({ userId: this.physician?.id, data }).then(() => {
                    this.isProcessingApproval = false
                })
                this.emitEvent({
                    name: 'privileges-updated',
                    payload: { userId: this.physician?.id, ...data },
                })
                this.closePracticePrivilegesModal()
            },

            modifyPrivileges() {
                this.isModifyingPrivileges = true
                this.openPracticePrivilegesModal()
            },

            practiceMemberRemoved(payload) {
                this.closeRemovePracticeMemberModal()
                this.emitEvent({ name: 'member-removed', payload })
            },

            invitationResent(payload) {
                this.toggleResendInvitationModal()
                this.emitEvent({ name: 'invitation-resent', payload })
            },

            selectionChanged(id) {
                this.emitEvent({ name: 'selection-changed', payload: id })
            },

            inviteUserToSubscribe(id) {
                UsersAPI.inivteUserToSubscribe(id).then(() => {
                    this.notificationMessage = this.$t('modals.make_direct_referal_modal.inivte_billing.invite_success')
                    this.$refs.commonSuccessNotification.showNotification()
                })
            },
        },
    }
</script>
